import { createElement, type CSSProperties, type ReactNode } from 'react';
import { alpha } from '@mui/material/styles';

/**
 * One chart vocabulary for both dashboards (Analytics and Tracking), so every
 * chart reads as one system: the reference palette from the dataviz skill
 * (references/palette.md, light mode), recessive grid and axes, 12px chart
 * text (the app's floor), and text in ink colours - never the series colour.
 *
 * Categorical hues are assigned in this fixed order and never cycled, so
 * "by the system" is always blue and "by hand" always orange on every chart
 * of both pages. Charts that need a different pair use aqua (slot 3) with
 * blue or violet (slot 7). Aqua is below 3:1 on the light surface, so a chart
 * using it also carries a legend and a tooltip or table.
 */
const CATEGORICAL = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'] as const;

export const SERIES = { system: CATEGORICAL[0], manual: CATEGORICAL[1], aqua: CATEGORICAL[2], violet: CATEGORICAL[6] };

/** "No email reply", "RFQs received" and other totals behind a split: a neutral, not a hue. */
export const NEUTRAL = '#c3c2b7';
/** Gridline hairline and the empty track of a bar meter. */
export const GRID = '#e1e0d9';
export const TRACK = '#f0efec';
export const BASELINE = '#c3c2b7';

export const INK = { primary: '#0b0b0b', secondary: '#52514e', muted: '#898781', good: '#006300', bad: '#d03b3b', check: '#b25e00' };

/** Sequential blue ramp, steps 100..700, for heatmaps. */
export const SEQUENTIAL = ['#cde2fb', '#b7d3f6', '#9ec5f4', '#86b6ef', '#6da7ec', '#5598e7', '#3987e5', '#2a78d6', '#256abf', '#1c5cab', '#184f95', '#104281', '#0d366b'];

/** Axis tick text: 12px secondary ink. */
export const AXIS = { fontSize: 12, fill: INK.secondary };

/** Hover band behind a bar group. */
export const CURSOR_FILL = alpha(SERIES.system, 0.08);

/** Chart plot heights: taller on large monitors so a wide card does not flatten its chart. */
export const CHART_HEIGHT = { xs: 240, lg: 260, xl: 300 } as const;

const legendLabel = (value: ReactNode) => createElement('span', { style: { color: INK.secondary } }, value);

/** Spread onto <Legend>: 12px labels in ink, small square keys. */
export const LEGEND = {
  iconSize: 10,
  wrapperStyle: { fontSize: 12, paddingTop: 4 } as CSSProperties,
  formatter: legendLabel,
};

/** Spread onto <Tooltip>: matches the app's paper and type scale. */
export const TOOLTIP = {
  contentStyle: { borderRadius: 8, border: '1px solid #e2e7ee', boxShadow: '0 4px 12px rgba(16, 24, 40, 0.08)', fontSize: 13, padding: '8px 10px' } as CSSProperties,
  labelStyle: { color: INK.primary, fontWeight: 600, marginBottom: 2 } as CSSProperties,
  itemStyle: { padding: 0 } as CSSProperties,
};

/** Spread onto stacked <Bar> marks: a 1px surface gap between segments. */
export const GAP = { stroke: '#fff', strokeWidth: 1 } as const;
